import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import {
    createParkingGuestReasonFormSchema,
    type CreateParkingGuestReasonFormSchemaValues,
} from 'src/features/parkingManagement/api/ReasonsForGuest/useCreateParkingGuestReason';
import {
    GUEST_REASONS_RESOURCE,
    type GuestReason,
} from 'src/features/parkingManagement/api/ReasonsForGuest/useGetParkingGuestReasons';
import { useUpdateParkingGuestReason } from 'src/features/parkingManagement/api/ReasonsForGuest/useUpdateParkingGuestReason';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps } from 'src/registry/components/table';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { Dispatch, SetStateAction } from 'react';

interface EditReasonForGuestProps extends DataTableNewEntryProps<any> {
    selectedReason: GuestReason;
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
}

const EditReasonForGuest = ({ selectedReason, isModalOpen, setIsModalOpen }: EditReasonForGuestProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { toast } = useToast();

    const form = useForm<CreateParkingGuestReasonFormSchemaValues>({
        resolver: zodResolver(createParkingGuestReasonFormSchema),
        defaultValues: {
            name: selectedReason.name,
        },
    });

    const { mutate, isPending } = useUpdateParkingGuestReason({
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [GUEST_REASONS_RESOURCE],
            });
            setIsModalOpen(false);
            toast({
                title: formatMessage(parkingManagementMessages.reasonUpdated),
                description: formatMessage(parkingManagementMessages.reasonUpdatedSuccessfully),
            });
        },
        onError: (e) => {
            const error = Object.entries(e?.response?.data || {})[0];
            const errorMessage = error
                ? `${error[0]}, ${error[1][0]}`
                : formatMessage(generalErrorMessages.somethingWentWrongRetry);

            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: errorMessage,
            });
        },
    });

    const onSubmit: SubmitHandler<CreateParkingGuestReasonFormSchemaValues> = (values) => {
        if (!parkingId) return;
        const submitValues = {
            parkingId: parkingId,
            id: selectedReason.id,
            ...values,
        };
        mutate(submitValues);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange} modal>
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={ICON_MAP.EDIT} />
                        <div>
                            <DialogTitle>{formatMessage(parkingManagementMessages.editReasonForGuest)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(parkingManagementMessages.editReasonForGuestDescription)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <Form form={form} variant="dialog" onSubmit={form.handleSubmit(onSubmit)}>
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog" required>
                                        {formatMessage(generalMessages.name)}
                                    </FormLabel>
                                    <FormControlStack className="mr-3">
                                        <FormControl>
                                            <Input
                                                type="text"
                                                placeholder={formatMessage(generalMessages.name)}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                    </Form>
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>

                    <Button type="submit" onClick={form.handleSubmit(onSubmit)} className="flex-1">
                        {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(parkingManagementMessages.editReasonForGuest)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default EditReasonForGuest;
